import React, { useState, useEffect } from 'react';
import { getRelatorioQuantitativo } from '../services/api';
import {
    Box, Typography, CircularProgress, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface RelatorioItem {
    descricao: string;
    quantidade_total: number;
    valor_total: number;
    preco_medio: number;
}

interface RelatorioFase {
    fase_nome: string;
    subtotal_fase: number;
    itens: RelatorioItem[];
}

interface RelatorioQuantitativoTabProps {
    obraId: number;
}

const RelatorioQuantitativoTab: React.FC<RelatorioQuantitativoTabProps> = ({ obraId }) => {
    const [data, setData] = useState<RelatorioFase[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const result = await getRelatorioQuantitativo(obraId);
                setData(result || []);
            } catch (err: any) {
                console.error(err);
                setError('Erro ao carregar relatório quantitativo.');
            } finally {
                setLoading(false);
            }
        };

        if (obraId) {
            fetchData();
        }
    }, [obraId]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value);
    };

    const formatNumber = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    if (!data || data.length === 0) return <Alert severity="info" sx={{ m: 2 }}>Nenhum dado encontrado para esta obra.</Alert>;

    const totalGeral = data.reduce((acc, fase) => acc + fase.subtotal_fase, 0);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Relatório Quantitativo por Fase</Typography>
                <Typography variant="h6" color="primary">Total Geral: {formatCurrency(totalGeral)}</Typography>
            </Box>

            {data.map((fase, index) => (
                <Accordion key={index} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{fase.fase_nome}</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{formatCurrency(fase.subtotal_fase)}</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Descrição do Item</TableCell>
                                        <TableCell align="right">Quantidade Total</TableCell>
                                        <TableCell align="right">Preço Médio</TableCell>
                                        <TableCell align="right">Valor Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fase.itens.map((item, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{item.descricao}</TableCell>
                                            <TableCell align="right">{formatNumber(item.quantidade_total)}</TableCell>
                                            <TableCell align="right">{formatCurrency(item.preco_medio)}</TableCell>
                                            <TableCell align="right">{formatCurrency(item.valor_total)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default RelatorioQuantitativoTab;
